import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { API_BASE_URL } from "../config/apiConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";

// Error toast
const ErrorToast = ({ error, onClose }) => {
  if (!error) return null;
  return (
    <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-md">
      <div className="flex justify-between items-center">
        <span>{error}</span>
        <button
          onClick={onClose}
          className="ml-4 text-red-700 hover:text-red-900"
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Google icon
const GoogleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

export default function SignUpWithGoogle() {
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState("")
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");

    if (ref) {
      setReferralCode(ref.toUpperCase(),
      );
    }
  }, [location.search]);

  // console.log(referralCode)
  const clearError = () => setError("");

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/users/google/auth`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ access_token: tokenResponse.access_token, referralCode })
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || `Server error: ${res.status}`);
        }

        const data = await res.json();
        console.log(data)
        localStorage.setItem("token", data.token);
        localStorage.setItem("fullName", data.fullName);
        localStorage.setItem("hasSeenModal", data.hasSeenModal);
        localStorage.setItem("accountType", data.accountType);
        localStorage.setItem("loginTime", Date.now());
        localStorage.setItem("sessionStart", new Date().toISOString());

        // Show role ID only for users who already have one (existing accounts).
        // Pick based on the actual accountType so a teacher/parent never sees TSA-*.
        const roleId =
          data.accountType === "teacher" ? data.teacher_id :
          data.accountType === "parent"  ? data.parent_id  :
          data.student_id;
        if (roleId) {
          localStorage.setItem("role_id", roleId);
          const roleLabel =
            data.accountType === "teacher" ? "Teacher ID" :
            data.accountType === "parent"  ? "Parent ID"  : "Student ID";
          if (!data.hasSeenModal) {
            toast.success(
              `Welcome, ${data.fullName}! Your ${roleLabel} is ${roleId} — write it down, you can use it to log in!`,
              { duration: 8000 }
            );
          }
        }

        // Auto-join teacher if student arrived via invite link
        const pendingTeacherCode = localStorage.getItem("pending_teacher_code");
        if (data.accountType === "student" && pendingTeacherCode) {
          try {
            const joinRes = await fetch(`${API_BASE_URL}/api/teachers/join/${pendingTeacherCode}`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.token}` },
            });
            if (joinRes.ok) localStorage.removeItem("pending_teacher_code");
            else console.error("Auto-join teacher error:", await joinRes.json());
          } catch (e) {
            console.error("Auto-join teacher failed:", e);
          }
        }

        // Auto-join parent if student arrived via parent invite link
        const pendingParentCode = localStorage.getItem("pending_parent_code");
        if (data.accountType === "student" && pendingParentCode) {
          try {
            const joinRes = await fetch(`${API_BASE_URL}/api/parents/join/${pendingParentCode}`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.token}` },
            });
            if (joinRes.ok) localStorage.removeItem("pending_parent_code");
            else console.error("Auto-join parent error:", await joinRes.json());
          } catch (e) {
            console.error("Auto-join parent failed:", e);
          }
        }

        if (data.accountType === "parent") {
          navigate("/parent-dashboard");
        } else if (data.accountType === "teacher") {
          navigate("/teacher-dashboard");
        } else if (data.hasSeenModal) {
          navigate("/dashboard");
        } else {
          navigate("/onboarding");
        }

        setError("");
      } catch (error) {
        console.error("❌ Signup error:", error);
        setError(error.message || "Signup failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setError("Google signup was cancelled or failed. Please try again.");
      setIsLoading(false);
    },
    flow: "implicit"
  });

  return (
    <div className="w-full max-w-md mx-auto p-6">
      <ErrorToast error={error} onClose={clearError} />

      <button
        onClick={login}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 bg-transparent border-2 border-blue-500 bg-white text-blue-600 rounded-lg px-6 py-3 font-medium hover:bg-blue-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
            <span>Signing up...</span>
          </>
        ) : (
          <>
            <GoogleIcon />
            <span>Sign up with Google</span>
          </>
        )}
      </button>
    </div>
  );
}
